/**
 * "Delivery Info" card.
 *
 * Talks to the Delhivery-backed proxy through Shopify's App Proxy, so the call
 * is same-origin and no token reaches the browser. Fires on demand only - there
 * is no work on page load beyond binding listeners and, for a returning
 * customer, re-checking the pincode they already gave us.
 *
 * The card owns the generic estimate too (see the snippet), so nothing here
 * reaches outside its own root to hide theme markup.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ta_delivery_pin';
  var PIN_RE = /^[1-9][0-9]{5}$/;

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function formatRange(fromISO, toISO) {
    var from = new Date(fromISO + 'T00:00:00');
    var to = new Date(toISO + 'T00:00:00');

    var fromLabel = MONTHS[from.getMonth()] + ' ' + from.getDate();
    var toLabel =
      from.getMonth() === to.getMonth()
        ? String(to.getDate())
        : MONTHS[to.getMonth()] + ' ' + to.getDate();

    return fromLabel + ' – ' + toLabel;
  }

  /**
   * A pincode we already know, so the customer never types one twice:
   *   1. whatever they last entered here (localStorage), then
   *   2. a logged-in customer's default address, handed over by
   *      snippets/plp-delivery-eta.liquid.
   * Nothing to go on -> null, and the card opens in its "Enter Pin code" state.
   */
  function readSavedPin() {
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved && PIN_RE.test(saved)) return saved;
    } catch (e) {
      /* private mode - fall through to the account address */
    }

    var context = document.querySelector('[data-delivery-context]');
    var fromAccount = context && context.dataset.customerPin;
    return fromAccount && PIN_RE.test(fromAccount) ? fromAccount : null;
  }

  function savePin(pin) {
    try {
      localStorage.setItem(STORAGE_KEY, pin);
    } catch (e) {
      /* ignore */
    }
  }

  function setup(root) {
    if (root.dataset.deliveryBound) return;
    root.dataset.deliveryBound = 'true';

    var intro = root.querySelector('[data-delivery-prompt]');
    var summary = root.querySelector('[data-delivery-summary]');
    var pinLabel = root.querySelector('[data-delivery-pin-label]');
    var dateEl = root.querySelector('[data-delivery-date]');
    var editor = root.querySelector('[data-delivery-editor]');
    var input = root.querySelector('[data-delivery-input]');
    var button = root.querySelector('[data-delivery-submit]');
    var resultEl = root.querySelector('[data-delivery-result]');
    var endpoint = root.dataset.endpoint;

    var inFlight = null;
    var currentPin = readSavedPin();

    function openEditor() {
      editor.hidden = false;
      input.value = currentPin || '';
      input.focus();
      // Preselected, so typing replaces the old pincode instead of appending.
      input.select();
    }

    function showMessage(text, isError) {
      resultEl.textContent = text;
      resultEl.className = 'delivery-info__result' + (isError ? ' delivery-info__error' : ' delivery-info__muted');
    }

    function clearMessage() {
      resultEl.textContent = '';
      resultEl.className = 'delivery-info__result';
    }

    function showAnswer(pin, range) {
      currentPin = pin;
      pinLabel.textContent = pin;
      dateEl.textContent = range;

      intro.hidden = true;
      summary.hidden = false;
      editor.hidden = true;
      clearMessage();
    }

    function render(data, pin) {
      if (!data.ok) {
        // Upstream is down. Fall back to the generic estimate rather than
        // leaving the card saying nothing at all.
        if (summary.hidden) intro.hidden = false;
        showMessage("Couldn't check right now. Please try again.", false);
        return;
      }

      if (!data.serviceable) {
        // "We don't deliver here" contradicts the generic estimate just as much
        // as a real date would, so that goes too. Editor stays open so they can
        // try another pincode straight away.
        intro.hidden = true;
        summary.hidden = true;
        showMessage("We don't deliver to this pincode yet.", true);
        return;
      }

      showAnswer(pin, formatRange(data.edd_from, data.edd_to));
    }

    function check(pin) {
      if (!PIN_RE.test(pin)) {
        showMessage('Enter a valid 6-digit pincode.', true);
        return;
      }

      savePin(pin);

      // Supersede any request still in flight so a fast second submit can't
      // land out of order and render a stale pincode's answer.
      if (inFlight) inFlight.abort();
      var controller = new AbortController();
      inFlight = controller;

      root.classList.add('is-loading');
      showMessage('Checking…', false);

      fetch(endpoint + '?pin=' + encodeURIComponent(pin), {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (controller.signal.aborted) return;
          render(data, pin);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          render({ ok: false }, pin);
        })
        .finally(function () {
          if (inFlight === controller) {
            inFlight = null;
            root.classList.remove('is-loading');
          }
        });
    }

    root.querySelectorAll('[data-delivery-open]').forEach(function (opener) {
      opener.addEventListener('click', openEditor);
    });

    button.addEventListener('click', function () {
      check(input.value.trim());
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        check(input.value.trim());
      }
    });

    // Strip anything non-numeric as the customer types.
    input.addEventListener('input', function () {
      var cleaned = input.value.replace(/\D/g, '').slice(0, 6);
      if (cleaned !== input.value) input.value = cleaned;
    });

    // Returning customers shouldn't retype their pincode on every product. The
    // date is re-fetched rather than cached, since it moves with the cutoff.
    if (currentPin) {
      intro.hidden = true;
      check(currentPin);
    }
  }

  function init() {
    document.querySelectorAll('[data-delivery-estimator]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Quick view and other AJAX-injected product forms mount after load.
  document.addEventListener('shopify:section:load', init);
})();
